import { useState, useEffect, useRef } from "react";
import {
  Globe, Camera, Upload, ExternalLink, Shield, ShieldOff,
  Star, Bookmark, Trash2, Pencil, Check, X, ChevronDown, Loader2, AlertCircle,
} from "lucide-react";
import { TINTA, MARCA, MONO } from "../constants.js";
import { normalizarUrl } from "../lib/util.js";
import {
  listarBookmarks, crearBookmark, renombrarBookmark, marcarUsoBookmark, borrarBookmark,
} from "../lib/api.js";

/* ============================================================
   NAVEGADOR — barra de URL, visor embebido (iframe) y acciones de captura.
   Modo de carga del iframe: directa o vía proxy del backend (S-010) con fallback.
   Marcadores (F6): guardados en el backend (SQLite) y sincronizados entre
   dispositivos (S-015); datalist, botón de estrella y lista con borrar/renombrar.
   ============================================================ */

// Espera antes de asumir que el sitio bloquea el iframe y pasar al proxy solo.
const TIMEOUT_FALLBACK_MS = 4000;

export default function Navegador({
  url,
  setUrl,
  urlCargada,
  onCargarSitio,
  onCapturar,
  onSubirClick,
  compartiendo,
  onDetenerCompartir,
  visorRef,
}) {
  const [viaProxy, setViaProxy] = useState(false);
  const timerRef = useRef(null);

  // --- Marcadores ---
  const [bookmarks, setBookmarks] = useState([]);
  const [cargandoBm, setCargandoBm] = useState(false);
  const [errorBm, setErrorBm] = useState("");
  const [menuAbierto, setMenuAbierto] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [textoEdit, setTextoEdit] = useState("");

  useEffect(() => {
    recargar();
  }, []);

  async function recargar() {
    setCargandoBm(true);
    setErrorBm("");
    try {
      setBookmarks(await listarBookmarks());
    } catch (e) {
      setErrorBm(e.message || "No se pudieron cargar los marcadores.");
    } finally {
      setCargandoBm(false);
    }
  }

  const urlNorm = normalizarUrl(url);
  const marcadorActual = bookmarks.find((b) => b.url === urlNorm) || null;

  async function alternarMarcador() {
    const u = normalizarUrl(url);
    if (!u) return;
    setErrorBm("");
    try {
      if (marcadorActual) await borrarBookmark(marcadorActual.id);
      else await crearBookmark(u, u);
      await recargar();
    } catch (e) {
      setErrorBm(e.message || "No se pudo guardar el marcador.");
    }
  }

  // Abre una URL (la del input, o `destino` si viene de un marcador) y, si estaba
  // guardada, actualiza su uso reciente para reordenar la lista.
  function abrir(destino) {
    onCargarSitio(destino);
    const u = normalizarUrl(destino ?? url);
    const bm = bookmarks.find((b) => b.url === u);
    if (bm) marcarUsoBookmark(bm.id).then(recargar).catch(() => {});
  }

  function abrirMarcador(b) {
    setUrl(b.url);
    setMenuAbierto(false);
    abrir(b.url);
  }

  async function guardarRenombre(id) {
    const t = textoEdit.trim();
    if (!t) return;
    setErrorBm("");
    try {
      await renombrarBookmark(id, t);
      setEditandoId(null);
      setTextoEdit("");
      await recargar();
    } catch (e) {
      setErrorBm(e.message || "No se pudo renombrar.");
    }
  }

  async function eliminar(id) {
    setErrorBm("");
    try {
      await borrarBookmark(id);
      await recargar();
    } catch (e) {
      setErrorBm(e.message || "No se pudo borrar.");
    }
  }

  // --- Modo de carga del iframe ---
  useEffect(() => {
    limpiarTimer();
    if (!urlCargada) return;
    setViaProxy(false);
    timerRef.current = setTimeout(() => setViaProxy(true), TIMEOUT_FALLBACK_MS);
    return limpiarTimer;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlCargada]);

  function limpiarTimer() {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }

  function alCargarIframe() {
    if (!viaProxy) limpiarTimer();
  }

  function alternarModo() {
    limpiarTimer();
    setViaProxy((v) => !v);
  }

  const srcVisor =
    urlCargada && viaProxy
      ? `/api/proxy?url=${encodeURIComponent(urlCargada)}`
      : urlCargada;

  const btnSecundario =
    "flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold border";

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Barra: dos filas para no desbordar en pantallas estrechas (F6). */}
      <div
        className="flex flex-col gap-2 p-3 bg-white border-b"
        style={{ borderColor: "#E2E5EB" }}
      >
        {/* Fila 1: URL + estrella + Abrir + marcadores */}
        <div className="flex items-center gap-2">
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && abrir()}
            placeholder="https://ejemplo.com"
            list="bm-urls"
            className="flex-1 min-w-0 border rounded-lg px-3 py-2 text-sm"
            style={{ borderColor: "#CBD2DC", fontFamily: MONO }}
          />
          <datalist id="bm-urls">
            {bookmarks.map((b) => (
              <option key={b.id} value={b.url}>
                {b.title}
              </option>
            ))}
          </datalist>

          <button
            onClick={alternarMarcador}
            disabled={!urlNorm}
            title={
              !urlNorm
                ? "Escribe una URL para guardarla"
                : marcadorActual
                ? "Quitar de marcadores"
                : "Guardar en marcadores"
            }
            className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-40"
            style={{ color: marcadorActual ? MARCA : TINTA }}
          >
            <Star size={18} fill={marcadorActual ? MARCA : "none"} />
          </button>

          <button
            onClick={() => abrir()}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white shrink-0"
            style={{ background: TINTA }}
          >
            Abrir
          </button>

          {/* Menú de marcadores */}
          <div className="relative">
            <button
              onClick={() => setMenuAbierto((v) => !v)}
              title="Marcadores"
              className="flex items-center gap-1 p-2 rounded-lg hover:bg-gray-100"
              style={{ color: TINTA }}
            >
              <Bookmark size={18} />
              <ChevronDown size={14} />
            </button>

            {menuAbierto && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setMenuAbierto(false)}
                />
                <div
                  className="absolute right-0 mt-1 w-72 max-h-80 overflow-auto z-20 bg-white border rounded-xl shadow-lg p-1"
                  style={{ borderColor: "#E2E5EB" }}
                >
                  <p
                    className="px-3 py-2 text-xs font-semibold tracking-wide"
                    style={{ color: "#6B7280", fontFamily: MONO }}
                  >
                    MARCADORES
                  </p>
                  {cargandoBm ? (
                    <div className="flex items-center gap-2 px-3 py-3 text-sm" style={{ color: "#5B6472" }}>
                      <Loader2 size={16} className="animate-spin" /> Cargando…
                    </div>
                  ) : bookmarks.length === 0 ? (
                    <p className="px-3 py-3 text-sm" style={{ color: "#5B6472" }}>
                      Aún no hay marcadores. Guarda uno con la ⭐.
                    </p>
                  ) : (
                    bookmarks.map((b) => (
                      <div
                        key={b.id}
                        className="group flex items-center gap-1 rounded-lg px-1 hover:bg-gray-50"
                      >
                        {editandoId === b.id ? (
                          <div className="flex items-center gap-1 flex-1 py-1">
                            <input
                              autoFocus
                              value={textoEdit}
                              onChange={(e) => setTextoEdit(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") guardarRenombre(b.id);
                                if (e.key === "Escape") setEditandoId(null);
                              }}
                              className="flex-1 min-w-0 border rounded-md px-2 py-1 text-sm"
                              style={{ borderColor: "#CBD2DC" }}
                            />
                            <button
                              onClick={() => guardarRenombre(b.id)}
                              className="p-1.5 rounded-md hover:bg-gray-100"
                              style={{ color: TINTA }}
                              title="Guardar nombre"
                            >
                              <Check size={15} />
                            </button>
                            <button
                              onClick={() => setEditandoId(null)}
                              className="p-1.5 rounded-md hover:bg-gray-100"
                              style={{ color: "#5B6472" }}
                              title="Cancelar"
                            >
                              <X size={15} />
                            </button>
                          </div>
                        ) : (
                          <>
                            <button
                              onClick={() => abrirMarcador(b)}
                              className="flex-1 min-w-0 text-left px-2 py-2"
                            >
                              <span className="block text-sm font-medium truncate" style={{ color: TINTA }}>
                                {b.title}
                              </span>
                              <span className="block text-[11px] truncate" style={{ color: "#8A93A2", fontFamily: MONO }}>
                                {b.url}
                              </span>
                            </button>
                            <button
                              onClick={() => {
                                setEditandoId(b.id);
                                setTextoEdit(b.title);
                              }}
                              className="p-1.5 rounded-md hover:bg-gray-100 opacity-60 hover:opacity-100"
                              style={{ color: TINTA }}
                              title="Renombrar"
                            >
                              <Pencil size={15} />
                            </button>
                            <button
                              onClick={() => eliminar(b.id)}
                              className="p-1.5 rounded-md hover:bg-gray-100 opacity-60 hover:opacity-100"
                              style={{ color: MARCA }}
                              title="Borrar"
                            >
                              <Trash2 size={15} />
                            </button>
                          </>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Aviso de error de marcadores (el backend puede fallar). */}
        {errorBm && (
          <div
            className="flex items-center gap-2 text-xs rounded-lg px-3 py-2"
            style={{ background: "#FDECEA", color: "#8A2A22" }}
          >
            <AlertCircle size={14} />
            <span className="flex-1">{errorBm}</span>
            <button onClick={() => setErrorBm("")} title="Cerrar">
              <X size={14} />
            </button>
          </div>
        )}

        {/* Fila 2: acciones (envuelven; no desbordan). */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={onCapturar}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-white"
            style={{ background: MARCA }}
          >
            <Camera size={16} /> <span>Capturar</span>
          </button>
          <button onClick={onSubirClick} className={btnSecundario} style={{ borderColor: TINTA, color: TINTA }}>
            <Upload size={16} /> <span className="hidden sm:inline">Subir imagen</span>
            <span className="sm:hidden">Subir</span>
          </button>
          {urlCargada && (
            <a
              href={urlCargada}
              target="_blank"
              rel="noreferrer"
              className={btnSecundario}
              title="Abrir en pestaña nueva"
              style={{ borderColor: "#CBD2DC", color: TINTA }}
            >
              <ExternalLink size={16} /> <span className="hidden sm:inline">Pestaña nueva</span>
            </a>
          )}
          {urlCargada && (
            <button
              onClick={alternarModo}
              title={
                viaProxy
                  ? "Cargando vía proxy (elimina el bloqueo de iframe). Pulsa para carga directa."
                  : "Carga directa. Pulsa para forzar el proxy si el sitio se ve en blanco."
              }
              className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-xs font-medium border"
              style={
                viaProxy
                  ? { background: TINTA, color: "#fff", borderColor: TINTA }
                  : { borderColor: "#CBD2DC", color: TINTA }
              }
            >
              {viaProxy ? <Shield size={15} /> : <ShieldOff size={15} />}
              <span>{viaProxy ? "Proxy" : "Directo"}</span>
            </button>
          )}
          {compartiendo && (
            <button
              onClick={onDetenerCompartir}
              title="Terminar sesión de captura"
              className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-xs font-medium border"
              style={{ borderColor: MARCA, color: MARCA }}
            >
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: MARCA }} />
              En vivo
            </button>
          )}
        </div>
      </div>

      <div
        ref={visorRef}
        className="flex-1 min-h-0 relative"
        style={{ background: "#E8EAEF" }}
      >
        {urlCargada ? (
          <iframe
            key={srcVisor}
            src={srcVisor}
            title="sitio"
            onLoad={alCargarIframe}
            className="w-full h-full border-0 bg-white"
          />
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-center p-6 gap-3">
            <Globe size={44} style={{ color: "#9AA3B2" }} />
            <p className="font-semibold">Escribe una URL para empezar la revisión</p>
            <p className="text-sm max-w-md" style={{ color: "#5B6472" }}>
              Navega el sitio aquí y pulsa <b>Capturar</b> cuando encuentres algo
              que comentar: la vista queda congelada con las herramientas de
              anotación encima. La primera vez el navegador pedirá permiso — elige{" "}
              <b>«Esta pestaña»</b> y las siguientes capturas serán instantáneas.
              En tablet, toma el pantallazo del dispositivo y usa <b>Subir imagen</b>.
            </p>
          </div>
        )}
        {urlCargada && (
          <div
            className="absolute bottom-3 left-3 right-3 sm:left-auto sm:max-w-sm bg-white/95 border rounded-lg px-3 py-2 text-xs shadow-sm"
            style={{ borderColor: "#E2E5EB", color: "#5B6472" }}
          >
            {viaProxy ? (
              <>
                Cargando por el <b>proxy</b>: se retiran los bloqueos de iframe. Si
                aún falla, ábrelo en pestaña nueva con{" "}
                <ExternalLink size={11} className="inline" />, toma tu pantallazo y
                súbelo aquí.
              </>
            ) : (
              <>
                Si el sitio se ve en blanco, bloquea ser embebido: prueba el modo{" "}
                <b>Proxy</b> aquí arriba, o ábrelo en pestaña nueva con{" "}
                <ExternalLink size={11} className="inline" />, toma tu pantallazo y
                súbelo aquí.
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
